// src/config/brand-overrides.ts
//
// Overrides de traducción por marca (white-label").
//
// El post-procesador `brandName` de i18n (ver config/i18n.ts) sustituye de
// forma genérica "Ainovis" -> productName y "Carolina" -> assistantName en
// TODAS las claves traducidas. Eso cubre el 99 % de los casos con un único
// juego de traducciones.
//
// Este archivo cubre la excepción: cuando una FRASE CONCRETA debe mostrar un
// nombre distinto (o un texto completamente distinto) en una marca concreta.
// El caso de uso original es el hero del panel del asistente:
//   "Pregúntame cualquier duda que tengas sobre Ainovis"
// que genéricamente pasaría a "...sobre ALMA" para ALMA, pero queremos que
// hable del ASISTENTE ("...sobre ALBA") y no del PRODUCTO.
//
// Mecánica: si existe override para (brand, lng, key), se aplica y se OMITE la
// sustitución genérica para esa clave. En caso contrario se sigue el flujo
// normal (brandName).

import type { BrandId } from './brand';

/**
 * Registro de overrides por marca.
 *
 * Estructura jerárquica: BRAND_TRANSLATION_OVERRIDES[brandId][lng][i18nKey].
 *
 * - `lng` se normaliza a su tronco de 2 letras ('es' a partir de 'es-ES').
 * - `i18nKey` es la clave completa tal como se pasa a `t()` (incluye el
 *   namespace, p. ej. "aiSidebar.heroText").
 */
export const BRAND_TRANSLATION_OVERRIDES: Partial<
  Record<BrandId, Partial<Record<string, Record<string, string>>>>
> = {
  ALMA_MEDICAL: {
    es: {
      'aiSidebar.heroText': 'Pregúntame cualquier duda que tengas sobre ALBA',
    },
    en: {
      'aiSidebar.heroText': 'Ask me any question you have about ALBA',
    },
    pt: {
      'aiSidebar.heroText': 'Pergunte-me qualquer dúvida que tenha sobre a ALBA',
    },
    it: {
      'aiSidebar.heroText': 'Chiedimi qualsiasi dubbio tu abbia su ALBA',
    },
    fr: {
      'aiSidebar.heroText': 'Posez-moi toutes vos questions sur ALBA',
    },
  },
};

/** Tronco de 2 letras del locale ('es-ES' -> 'es', 'en-US' -> 'en'). */
function normalizeLng(lng: string | undefined | null): string {
  return (lng ?? '').split('-')[0];
}

/**
 * Resuelve el override de traducción para una marca/idioma/clave dados.
 *
 * @returns El texto del override si existe, o `null` si no hay override (en
 *          cuyo caso el llamador debe caer al procesado genérico de marca).
 */
export function resolveBrandOverride(
  brandId: BrandId,
  lng: string | undefined | null,
  key: string,
): string | null {
  const byBrand = BRAND_TRANSLATION_OVERRIDES[brandId];
  if (!byBrand) return null;
  const byLng = byBrand[normalizeLng(lng)];
  if (!byLng) return null;
  return byLng[key] ?? null;
}
